import type { ReactNode, ReactElement } from 'react';

import { useState } from 'react';
// material
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogContentText,
  DialogTitle,
  DialogActions,
  DialogContent,
  Box,
} from '@mui/material';
// components
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import TourProvider from 'src/components/TourProvider';
import TourAutoStarter from 'src/components/TourAutoStarter';
// hooks
import Paywall from 'src/views/Paywall';
import { useHistory } from 'react-router';
import { PATH_TRAINING } from 'src/routes/paths';
import EmailSuppressionStatus from 'src/components/EmailSuppressionStatus';
import EinStatusAlert from 'src/components/EinStatusAlert';
import EinReminderDialog from 'src/components/EinReminderDialog';
import { IncomingRequestsProvider } from 'src/views/Donations/IncomingRequests';
import { ChatClientProvider } from 'src/hooks/useChatClient';
import { useTranslation } from 'react-i18next';
import useTrainingComplete from 'src/hooks/useTrainingComplete';
import useApprovalStatus from 'src/hooks/useApprovalStatus';
import PendingApprovalModal from 'src/components/PendingApprovalModal';
import { OnboardingRequestsProvider } from 'src/hooks/corporate-onboarding/useOnboardingRequests';
import { useTourContext } from 'src/components/TourProvider';

export interface DashboardLayoutProps {
  children?: ReactNode;
  [key: string]: any;
}

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 64;

// Scrolling happens on the window (Root/Main grow with their content), so
// neither wrapper may create a scroll container: `overflow: hidden`/`auto`
// here would become the scrollport for descendant `position: sticky`
// elements (e.g. the marketplace checkout/order summary rails) and, since
// these wrappers never scroll themselves, sticky would never engage.
// `clip` keeps the horizontal-bleed protection without a scroll container.
const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'clip',
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  // `overflow: auto` used to zero the flex-item minimum size; keep that so
  // wide content can't force the main column past the viewport.
  minWidth: 0,
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

function HideAllToursDialog() {
  const { t } = useTranslation('translation');
  const { hideAllToursModalOpen, hideAllTours, dismissHideAllToursPrompt } =
    useTourContext();

  return (
    <Dialog open={hideAllToursModalOpen} onClose={dismissHideAllToursPrompt}>
      <DialogTitle>{t('hide-all-tours.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('hide-all-tours.description')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismissHideAllToursPrompt}>
          {t('hide-all-tours.cancel')}
        </Button>
        <Button variant="contained" onClick={hideAllTours}>
          {t('hide-all-tours.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const history = useHistory();

  const { trainingComplete } = useTrainingComplete();
  const [trainingOpen, setTrainingOpen] = useState(true);
  const handleCloseTraining = () => setTrainingOpen(false);
  const handleOpenTraining = () => setTrainingOpen(true);
  const handleStartTraining = () => history.push(PATH_TRAINING.start);

  const { isPendingApproval, isLoading: approvalLoading } = useApprovalStatus();

  return (
    <RootStyle>
      <TourProvider>
        <TourAutoStarter />
        <ChatClientProvider>
          <IncomingRequestsProvider>
            <OnboardingRequestsProvider>
              <DashboardNavbar
                onOpenSidebar={() => setOpen(true)}
                trainingComplete={trainingComplete}
                handleOpenTraining={handleOpenTraining}
              />
              <DashboardSidebar
                isOpenSidebar={open}
                onCloseSidebar={() => setOpen(false)}
              />
              <MainStyle>
                <Paywall>
                  <Box my={2}>
                    <EmailSuppressionStatus />
                  </Box>
                  <Box my={2}>
                    <EinStatusAlert />
                  </Box>

                  {children}
                </Paywall>
              </MainStyle>

              <Dialog
                open={!trainingComplete && trainingOpen}
                onClose={handleCloseTraining}
              >
                <DialogTitle>{t('training-program.title')}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {t('training-program.description')}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseTraining}>
                    {t('training-program.not-now')}
                  </Button>
                  <Button variant="contained" onClick={handleStartTraining}>
                    {t('training-program.start-training')}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Pending Approval Modal */}
              <PendingApprovalModal
                open={isPendingApproval && !approvalLoading}
              />
              <EinReminderDialog />
              <HideAllToursDialog />
            </OnboardingRequestsProvider>
          </IncomingRequestsProvider>
        </ChatClientProvider>
      </TourProvider>
    </RootStyle>
  );
}
